import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { SlidersHorizontal, X, Heart } from "lucide-react";
import axios from "axios";
import Navbar from "components/layout/Navbar";
import Footer from "components/layout/Footer";
import ProductCard from "components/product/ProductCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "components/ui/sheet";
import { Slider } from "components/ui/slider";
import { CATEGORIES } from "constants/categories";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShopPage = () => {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [priceRange, setPriceRange] = useState([0, 50000]);
  const [sortBy, setSortBy] = useState("newest");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const search = searchParams.get("search") || "";
  const selectedCategory = category || searchParams.get("category") || "";

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.8 },
  };

  const staggerContainer = {
    whileInView: {
      transition: { staggerChildren: 0.1 },
    },
  };

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (selectedCategory) params.append("category", selectedCategory);
        if (search) params.append("search", search);
        if (priceRange[0] > 0) params.append("min_price", priceRange[0]);
        if (priceRange[1] < 50000) params.append("max_price", priceRange[1]);

        const response = await axios.get(
          `${API}/products?${params.toString()}`,
        );
        let sortedProducts = [...response.data.products];

        // Client-side sorting
        switch (sortBy) {
          case "price_low":
            sortedProducts.sort((a, b) => a.price - b.price);
            break;
          case "price_high":
            sortedProducts.sort((a, b) => b.price - a.price);
            break;
          case "name":
            sortedProducts.sort((a, b) => a.name.localeCompare(b.name));
            break;
          default:
            // newest - keep original order
            break;
        }

        setProducts(sortedProducts);
        setTotal(response.data.total);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [selectedCategory, search, priceRange, sortBy]);

  const handleCategoryChange = (cat) => {
    if (cat === "all") {
      setSearchParams({});
    } else {
      setSearchParams({ category: cat });
    }
  };

  const clearFilters = () => {
    setSearchParams({});
    setPriceRange([0, 50000]);
    setSortBy("newest");
  };

  const getCategoryName = (id) => {
    const cat = CATEGORIES.find((c) => c.id === id);
    return cat?.name || id?.replace("_", " ");
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const FilterContent = () => (
    <div className="space-y-8">
      {/* Categories */}
      <div>
        <h3 className="text-sm tracking-[0.1em] uppercase text-[#4F3535] font-medium mb-4">
          Categories
        </h3>
        <div className="space-y-1">
          <button
            onClick={() => handleCategoryChange("all")}
            className={`block w-full text-left py-2.5 px-3 transition-all duration-300 ${
              !selectedCategory
                ? "bg-[#6A4B4B] text-white"
                : "text-[#4F3535] hover:bg-[#4F3535]/5"
            }`}
            data-testid="filter-all"
          >
            All Products
          </button>
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => handleCategoryChange(cat.id)}
              className={`block w-full text-left py-2.5 px-3 transition-all duration-300 ${
                selectedCategory === cat.id
                  ? "bg-[#6A4B4B] text-white"
                  : "text-[#4F3535] hover:bg-[#4F3535]/5"
              }`}
              data-testid={`filter-${cat.id}`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      </div>
      {/* Price Range */}
      {/* <div>
        <h3 className="text-sm tracking-[0.1em] uppercase text-[#4F3535] font-medium mb-4">Price Range</h3>
        <Slider
          value={priceRange}
          onValueChange={setPriceRange}
          max={50000}
          step={500}
          className="mb-4"
        />
        <div className="flex items-center justify-between text-sm text-[#8A7B7B]">
          <span>{formatPrice(priceRange[0])}</span>
          <span>{formatPrice(priceRange[1])}</span>
        </div> */}
      {/* </div> */}
      Clear Filters
      <button
        onClick={clearFilters}
        className="w-full border border-[#4F3535] text-[#4F3535] hover:bg-[#4F3535] hover:text-white transition-all duration-300 px-8 py-3 tracking-[0.2em] uppercase text-sm"
        data-testid="clear-filters-btn"
      >
        Clear All Filters
      </button>
    </div>
  );

  return (
    <div className="bg-[#F4EBE1] text-[#4F3535] min-h-screen">
      <Navbar />

      <div className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Header */}
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h1 className="font-serif text-4xl md:text-5xl font-medium text-[#4F3535] leading-[1.1] mb-6">
              {selectedCategory
                ? getCategoryName(selectedCategory)
                : "Our Collection"}
            </h1>
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="h-px bg-[#4F3535]/20 w-16"></div>
              <Heart className="w-4 h-4 text-[#6A4B4B] fill-[#6A4B4B]" />
              <div className="h-px bg-[#4F3535]/20 w-16"></div>
            </div>
            <p className="text-[#8A7B7B] text-sm tracking-widest uppercase">
              {total} {total === 1 ? "piece" : "pieces"}
              {search && (
                <>
                  {" "}
                  matching "
                  <span className="text-[#4F3535] font-medium">{search}</span>"
                </>
              )}
            </p>
          </motion.div>

          <div className="flex gap-12">
            {/* Desktop Sidebar */}
            <aside className="hidden lg:block w-64 flex-shrink-0">
              <div className="sticky top-28">
                <FilterContent />
              </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1">
              {/* Toolbar */}
              <div className="flex items-center justify-between mb-10 pb-4 border-b border-[#4F3535]/10">
                {/* Mobile Filter Button */}
                <Sheet
                  open={mobileFiltersOpen}
                  onOpenChange={setMobileFiltersOpen}
                >
                  <SheetTrigger asChild>
                    <button
                      className="lg:hidden flex items-center gap-2 text-[#4F3535] text-sm tracking-widest uppercase"
                      data-testid="mobile-filters-btn"
                    >
                      <SlidersHorizontal className="w-4 h-4" />
                      Filters
                    </button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-80 bg-[#F4EBE1]">
                    <SheetHeader>
                      <SheetTitle className="text-[#4F3535] font-serif text-xl">
                        Filters
                      </SheetTitle>
                    </SheetHeader>
                    <div className="mt-6">
                      <FilterContent />
                    </div>
                  </SheetContent>
                </Sheet>

                {/* Active Filters */}
                <div className="hidden lg:flex items-center gap-2">
                  {selectedCategory && (
                    <span className="inline-flex items-center gap-2 bg-[#4F3535]/5 border border-[#4F3535]/10 px-4 py-1.5 text-sm text-[#4F3535] tracking-wider uppercase">
                      {getCategoryName(selectedCategory)}
                      <button
                        onClick={() => handleCategoryChange("all")}
                        className="hover:text-[#6A4B4B] transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                </div>

                {/* Sort */}
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger
                    className="w-[180px] border-[#4F3535]/15 bg-transparent text-[#4F3535]"
                    data-testid="sort-select"
                  >
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FAF7F3] border-[#EAE2DA]">
                    <SelectItem value="newest">Newest</SelectItem>
                    <SelectItem value="price_low">
                      Price: Low to High
                    </SelectItem>
                    <SelectItem value="price_high">
                      Price: High to Low
                    </SelectItem>
                    <SelectItem value="name">Name: A-Z</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Products Grid */}
              {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-12 gap-y-20">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="animate-pulse">
                      <div className="aspect-[3/4] bg-[#4F3535]/5 mb-4"></div>
                      <div className="h-4 bg-[#4F3535]/10 w-3/4 mb-2 mx-auto"></div>
                      <div className="h-4 bg-[#4F3535]/10 w-1/4 mx-auto"></div>
                    </div>
                  ))}
                </div>
              ) : products.length > 0 ? (
                <motion.div
                  className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-12 gap-y-20"
                  variants={staggerContainer}
                  initial="initial"
                  whileInView="whileInView"
                  viewport={{ once: true }}
                >
                  {products.map((product) => (
                    <motion.div
                      key={product.product_id}
                      variants={{
                        initial: { opacity: 0, y: 20 },
                        whileInView: {
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0.8 },
                        },
                      }}
                    >
                      <ProductCard product={product} />
                    </motion.div>
                  ))}
                </motion.div>
              ) : (
                <motion.div className="text-center py-20" {...fadeInUp}>
                  <Heart className="w-8 h-8 text-[#4F3535]/20 mx-auto mb-6" />
                  <p className="text-[#8A7B7B] mb-6 font-serif italic text-lg">
                    No pieces found matching your criteria.
                  </p>
                  <button
                    onClick={clearFilters}
                    className="bg-[#6A4B4B] text-white hover:bg-[#4F3535] transition-colors duration-300 px-12 py-4 tracking-[0.2em] uppercase text-sm font-medium"
                  >
                    Clear Filters
                  </button>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ShopPage;
